import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const routeHead = {
  meta: [
    { title: "Contact — PSY BLOOM Institute" },
    {
      name: "description",
      content:
        "Contacteaza PSY BLOOM Institute. Adresa cabinetului din Bucuresti, email si harta cu locatia.",
    },
    { property: "og:title", content: "Contact — PSY BLOOM Institute" },
    {
      property: "og:description",
      content:
        "Adresa, email si harta cabinetului PSY BLOOM Institute din Bucuresti.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "/contact" },
  ],
  links: [{ rel: "canonical", href: "https://psybloominstitute.ro/contact" }],
  scripts: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        name: "PSY BLOOM Institute",
        url: "https://psybloominstitute.ro/contact",
        email: "contact@psybloominstitute.ro",
        address: { "@type": "PostalAddress", addressLocality: "Bucuresti", addressCountry: "RO" },
        geo: { "@type": "GeoCoordinates", latitude: 44.449608, longitude: 26.119539 },
      }),
    },
  ],
};

export const Route = createFileRoute("/contact")({
  component: ContactPage,
});

function ContactPage() {
  useRouteHead(routeHead);
  const mapSrc = `https://www.google.com/maps?q=44.449608,26.119539&z=16&output=embed`;

  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-9 w-auto" width={180} height={36} />
          </Link>
          <nav aria-label="Navigare principala" className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" hash="despre" className="hover:text-foreground transition-colors">Despre noi</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Blog</a>
            <div className="relative group">
              <button type="button" className="hover:text-foreground transition-colors inline-flex items-center gap-1">
                Specialisti
                <span aria-hidden="true" className="text-xs">▾</span>
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-all">
                <div className="bg-background border border-border rounded-xl shadow-[var(--shadow-card)] py-2 min-w-[200px]">
                  <Link to="/anca-vlad" className="block px-4 py-2 hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap">Anca Vlad</Link>
                  <Link to="/nicolle-gheorghiu" className="block px-4 py-2 hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap">Nicolle Gheorghiu</Link>
                </div>
              </div>
            </div>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="py-16 md:py-24 bg-white">
          <div className="mx-auto max-w-6xl px-6">
            <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
              <h1 className="text-4xl md:text-5xl mb-4 text-balance">Contact</h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Ne poti gasi la cabinetul PSY BLOOM Institute din Bucuresti sau ne poti scrie pe email.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <address className="not-italic space-y-6 text-base md:text-lg">
                <div>
                  <h2 className="text-sm uppercase tracking-wider text-teal font-medium mb-2 font-sans">Adresa</h2>
                  <p className="text-foreground whitespace-pre-line">Intrarea Precupeții Vechi nr 6, etaj 2, sector 2</p>
                </div>
                <div>
                  <h2 className="text-sm uppercase tracking-wider text-teal font-medium mb-2 font-sans">Email</h2>
                  <a href="mailto:contact@psybloominstitute.ro" className="text-foreground hover:text-teal transition-colors">
                    contact@psybloominstitute.ro
                  </a>
                </div>
              </address>
              <div className="rounded-3xl overflow-hidden border border-border shadow-[var(--shadow-card)] aspect-[4/3] lg:aspect-[16/10]">
                <iframe
                  src={mapSrc}
                  title="Locatia PSY BLOOM Institute pe harta"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <Link to="/" hash="despre" className="hover:text-white transition-colors">Despre</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni si conditii</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
